import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import type { ApiClient } from "../types/UserTypes";
import HistoryCorrectionModal, { type HistoryCorrectionMode } from "./HistoryCorrectionModal";
import styles from "./HistoricalReportingHistory.module.css";

type HistoryScope = "all" | "current" | "upcoming" | "historical";

export type HistoryValueField = {
  key: string;
  label: string;
  format?: (value: unknown, row: HistoryRow) => string;
};

export type HistoryEndpointConfig = {
  readPath: string;
  correctionPath: string;
  valueFields: HistoryValueField[];
  sectionLabel?: string;
  rowScopeFields?: string[];
  rowExcludeFields?: string[];
};

type HistoryRow = {
  id?: number;
  effectiveStartDate?: string;
  effectiveEndDate?: string | null;
  isCurrent?: boolean;
  isUpcoming?: boolean;
  createdAt?: string;
  __historyConfigIndex?: number;
  __historySectionLabel?: string;
  [key: string]: unknown;
};

type HistoricalReportingHistoryProps = {
  show: boolean;
  api: ApiClient | null;
  enabled?: boolean;
  title: string;
  contextLabel: string;
  config: HistoryEndpointConfig | null;
  configs?: HistoryEndpointConfig[];
  onClose: () => void;
  onChanged?: () => void;
  onCorrected?: (message: string) => void;
};

type CorrectionState = {
  show: boolean;
  mode: HistoryCorrectionMode;
  row: HistoryRow | null;
  saving: boolean;
  error: string;
  conflictDate: string;
};

const SCOPE_OPTIONS: Array<{ value: HistoryScope; label: string }> = [
  { value: "all", label: "All" },
  { value: "current", label: "Current" },
  { value: "upcoming", label: "Upcoming" },
  { value: "historical", label: "Historical" },
];

const WARNING_COPY =
  "Corrections affect future report reruns. Existing generated invoice snapshots will not change unless deleted and regenerated.";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value?: string | null) => {
  if (!value) return "Present";
  const dateOnly = DATE_ONLY_PATTERN.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatYmd = (value?: string | null) => {
  if (!value) return "";
  return value.slice(0, 10);
};

const getErrorMessage = (err: unknown, fallback: string) => {
  const error = err as { message?: string; details?: unknown };
  if (typeof error?.details === "string" && error.details.trim()) return error.details.replace(/^"|"$/g, "");
  if (error?.details && typeof error.details === "object") {
    const details = error.details as Record<string, unknown>;
    if (typeof details.message === "string" && details.message.trim()) return details.message;
    if (typeof details.detail === "string" && details.detail.trim()) return details.detail;
    if (typeof details.title === "string" && details.title.trim()) return details.title;
  }
  return error?.message || fallback;
};

const isEffectiveDateConflict = (err: unknown) => {
  const error = err as { code?: unknown };
  return error?.code === "EFFECTIVE_DATE_CONFLICT";
};

const rowHasScopedValue = (row: HistoryRow, config: HistoryEndpointConfig) => {
  const excludeFields = config.rowExcludeFields || [];
  const hasExcludedValue = excludeFields.some((field) => {
    const raw = row[field];
    return raw !== null && raw !== undefined && raw !== "";
  });
  if (hasExcludedValue) return false;

  const scopeFields = config.rowScopeFields && config.rowScopeFields.length > 0
    ? config.rowScopeFields
    : config.valueFields.map((field) => field.key);

  return scopeFields.some((field) => {
    const raw = row[field];
    return raw !== null && raw !== undefined && raw !== "";
  });
};

export const formatCurrency = (value: unknown) => {
  const amount = Number(value || 0);
  return `$${amount.toFixed(2)}`;
};

export const formatPercentOrCurrency = (value: unknown, row: HistoryRow) => {
  const amount = Number(value || 0);
  return row.adminFeeType === "flat" ? `$${amount.toFixed(2)}` : `${amount}%`;
};

export const HistoryIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4.167 5.833V2.5M4.167 5.833H7.5M4.42 6.083A6.667 6.667 0 1 1 3.333 10"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 6.667V10l2.5 1.667"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const HistoryIconButton = ({
  onClick,
  label,
  disabled,
  testId,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  testId?: string;
}) => (
  <button
    type="button"
    className={styles["history-icon-btn"]}
    onClick={onClick}
    aria-label={label}
    title="View effective history"
    disabled={disabled}
    data-testid={testId}
  >
    <HistoryIcon />
  </button>
);

const HistoricalReportingHistory = ({
  show,
  api,
  enabled = true,
  title,
  contextLabel,
  config,
  configs,
  onClose,
  onChanged,
  onCorrected,
}: HistoricalReportingHistoryProps) => {
  const [scope, setScope] = useState<HistoryScope>("all");
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [correction, setCorrection] = useState<CorrectionState>({
    show: false,
    mode: "change-date",
    row: null,
    saving: false,
    error: "",
    conflictDate: "",
  });
  const configsList = useMemo(
    () => (configs && configs.length > 0 ? configs : config ? [config] : []),
    [config, configs],
  );

  const loadHistory = useCallback(async () => {
    if (!enabled) {
      setRows([]);
      setError("");
      return;
    }
    if (!show || !api || configsList.length === 0) return;
    setLoading(true);
    setError("");
    try {
      const results = await Promise.all(
        configsList.map(async (item, configIndex) => {
          const data = await api.get<HistoryRow[]>(item.readPath, { query: { scope } });
          return (Array.isArray(data) ? data : [])
            .filter((row) => rowHasScopedValue(row, item))
            .map((row) => ({
              ...row,
              __historyConfigIndex: configIndex,
              __historySectionLabel: item.sectionLabel,
            }));
        }),
      );
      setRows(results.flat());
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load effective history."));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, configsList, enabled, scope, show]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!show) {
      setRows([]);
      setError("");
      setScope("all");
      setCorrection({ show: false, mode: "change-date", row: null, saving: false, error: "", conflictDate: "" });
    }
  }, [show]);

  const titleId = "historicalReportingHistoryTitle";

  const rowSummary = useMemo(() => {
    const row = correction.row;
    if (!row) return "";
    const rowConfig = configsList[row.__historyConfigIndex ?? 0];
    if (!rowConfig) return "";
    const values = rowConfig.valueFields
      .map((field) => {
        const raw = row[field.key];
        if (raw === null || raw === undefined || raw === "") return null;
        const formatted = field.format ? field.format(raw, row) : String(raw);
        return `${field.label}: ${formatted}`;
      })
      .filter(Boolean);
    const range = `${formatDate(row.effectiveStartDate)} - ${formatDate(row.effectiveEndDate)}`;
    return [range, ...values].join(" | ");
  }, [configsList, correction.row]);

  const handleCorrectionSuccess = async (message: string) => {
    setCorrection({ show: false, mode: "change-date", row: null, saving: false, error: "", conflictDate: "" });
    await loadHistory();
    onChanged?.();
    onCorrected?.(message);
  };

  const changeDate = async (effectiveStartDate: string) => {
    const rowConfig = configsList[correction.row?.__historyConfigIndex ?? 0];
    if (!api || !rowConfig || !correction.row?.id) return;
    setCorrection((prev) => ({ ...prev, saving: true, error: "", conflictDate: "" }));
    try {
      await api.put(`${rowConfig.correctionPath}/${correction.row.id}/effective-date`, { effectiveStartDate });
      await handleCorrectionSuccess("Effective date changed.");
    } catch (err) {
      const message = getErrorMessage(err, "Failed to change effective date.");
      setCorrection((prev) => ({
        ...prev,
        saving: false,
        error: isEffectiveDateConflict(err)
          ? `${message} Choose another date or edit the existing history row.`
          : message,
        conflictDate: isEffectiveDateConflict(err) ? effectiveStartDate : "",
      }));
    }
  };

  const deleteRow = async () => {
    const rowConfig = configsList[correction.row?.__historyConfigIndex ?? 0];
    if (!api || !rowConfig || !correction.row?.id) return;
    setCorrection((prev) => ({ ...prev, saving: true, error: "", conflictDate: "" }));
    try {
      await api.delete(`${rowConfig.correctionPath}/${correction.row.id}`);
      await handleCorrectionSuccess("History row deleted.");
    } catch (err) {
      setCorrection((prev) => ({
        ...prev,
        saving: false,
        error: getErrorMessage(err, "Failed to delete history row."),
      }));
    }
  };

  return (
    <>
      <Modal
        show={enabled && show && !correction.show}
        onHide={onClose}
        size="xl"
        centered
        scrollable
        dialogClassName={styles["history-modal"]}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="historical-reporting-history"
      >
        <Modal.Header closeButton className={styles["history-header"]}>
          <Modal.Title id={titleId} className={styles["history-title"]}>
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles["history-body"]}>
          <p className={styles["history-context"]}>{contextLabel}</p>
          <div className={styles["history-warning"]}>{WARNING_COPY}</div>

          <div className={styles["history-tabs"]} role="tablist" aria-label="History scope">
            {SCOPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`${styles["history-tab"]}${scope === option.value ? ` ${styles["history-tab--active"]}` : ""}`}
                onClick={() => setScope(option.value)}
                aria-pressed={scope === option.value}
                data-testid={`history-scope-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className={styles["history-table"]}>
            <div className={styles["history-row-header"]}>
              <div>Status</div>
              <div>Effective Range</div>
              <div>Values</div>
              <div>Created</div>
              <div>Actions</div>
            </div>
            {loading ? (
              <div className={styles["history-loading"]}>Loading effective history...</div>
            ) : error ? (
              <div className={styles["history-error"]} role="alert">{error}</div>
            ) : rows.length === 0 ? (
              <div className={styles["history-empty"]}>No effective history found.</div>
            ) : (
              rows.map((row, index) => {
                const rowConfig = configsList[row.__historyConfigIndex ?? 0];
                const statusLabel = row.isCurrent ? "Current" : row.isUpcoming ? "Upcoming" : "Historical";
                const statusClass = row.isCurrent
                  ? styles["history-status--current"]
                  : row.isUpcoming
                    ? styles["history-status--upcoming"]
                    : "";
                return (
                  <div className={styles["history-row"]} key={row.id ?? index} data-testid={`history-row-${row.id ?? index}`}>
                    <div>
                      <span className={`${styles["history-status"]} ${statusClass}`}>{statusLabel}</span>
                    </div>
                    <div>{formatDate(row.effectiveStartDate)} - {formatDate(row.effectiveEndDate)}</div>
                    <div className={styles["history-values"]}>
                      {row.__historySectionLabel && (
                        <span className={`${styles["history-value"]} ${styles["history-value--section"]}`}>
                          {row.__historySectionLabel}
                        </span>
                      )}
                      {rowConfig?.valueFields.map((field) => {
                        const raw = row[field.key];
                        if (raw === null || raw === undefined || raw === "") return null;
                        return (
                          <span className={styles["history-value"]} key={field.key}>
                            {field.label}: {field.format ? field.format(raw, row) : String(raw)}
                          </span>
                        );
                      })}
                    </div>
                    <div>{formatDate(row.createdAt)}</div>
                    <div className={styles["history-actions"]}>
                      {enabled && (
                      <>
                      <button
                        type="button"
                        className={styles["history-action"]}
                        onClick={() => setCorrection({ show: true, mode: "change-date", row, saving: false, error: "", conflictDate: "" })}
                        data-testid={`history-change-date-${row.id ?? index}`}
                      >
                        Change date
                      </button>
                      <button
                        type="button"
                        className={styles["history-action-danger"]}
                        onClick={() => setCorrection({ show: true, mode: "delete", row, saving: false, error: "", conflictDate: "" })}
                        data-testid={`history-delete-${row.id ?? index}`}
                      >
                        Delete
                      </button>
                      </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className={styles["history-footer"]}>
          <button
            type="button"
            className={styles["history-close-btn"]}
            onClick={onClose}
            data-testid="history-close"
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <HistoryCorrectionModal
        show={enabled && correction.show}
        mode={correction.mode}
        rowSummary={rowSummary}
        currentEffectiveDate={formatYmd(correction.row?.effectiveStartDate)}
        saving={correction.saving}
        errorMessage={correction.error}
        conflictEffectiveDate={correction.conflictDate}
        onCancel={() => setCorrection({ show: false, mode: "change-date", row: null, saving: false, error: "", conflictDate: "" })}
        onConfirmChangeDate={changeDate}
        onConfirmDelete={deleteRow}
      />
    </>
  );
};

export default HistoricalReportingHistory;
